/**
 * @name Emp.jsx
 * @author 김권영
 * @since 2025. 02. 26.
 * @description
 * 사업장(관리자 조회), 공정별 설비의 설비 작업 관련 직원 매핑 관리 (이해관계자)
 * 공정별 설비 목록 조회, 설비 작업 관련 직원 조회, 선택 후 매핑
 *
 * @param {string} adminCompCode - 관리자 : 선택된 사업장 코드 전역으로 관리
 * @param {string} realCompCode - 관리자 : 선택된 회사 코드(전역), 유저 : 로그인 정보에 담긴 회사 코드
 * @param {string} realSiteCode - 관리자 : 선택된 사업장 코드(전역), 유저 : 로그인 정보에 담긴 사업장 코드
 * @param {string} deptCode - 검색 조건 부서
 * @param {string} searchWrd - 검색 조건 이름
 */
import React, { useEffect, useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Input, Select, Checkbox, ProcEqptList, CompanySearchModal, SiteSearchModal } from "@components";
import { AuthContext, useAdminCompany, useSiteSelection } from "@context";
import { useSupvLogic } from "@hooks/acci/useSupvLogic";
import { ROLES } from "@constants/roles";
import { get } from "@utils/axios_api";
import { toast } from 'react-toastify';
import "./acci.css";
const Supv = () => {
    // 사용자 권한
    const { auth, hasPermission } = useContext(AuthContext);
    const canWrite = hasPermission('ACCIDENT_WRITE');
    const isAdmin = auth?.role === ROLES.SUPER_ADMIN || auth?.role === ROLES.ADMIN;

    // 전역 선택한 회사 번호 (관리자)
    const {adminCompCode, adminCompName} = useAdminCompany();

    // 재해관리 공통 회사/사업장 컨텍스트
    const {
        compCode: acciCompCode,
        compName: acciCompName,
        siteCode: acciSiteCode,
        siteName: acciSiteName,
        setSiteSelection: setAcciCompany,
        clearSiteSelection: clearAcciCompany,
    } = useSiteSelection();

    const methods = useForm({
        defaultValues: {
            deptCode: "",
            searchWrd: "",

        },
    });
    // 폼 값 추적
    const deptCode = methods.watch("deptCode");
    const searchWrd = methods.watch("searchWrd");



    // 일반 사용자의 회사 정보
    const [userSiteList, setUserSiteList] = useState([]);
    const [selectedUserSite, setSelectedUserSite] = useState(acciSiteCode || "");

    const [selectedSite, setSelectedSite] = useState(
        acciSiteCode ? { siteCode: acciSiteCode, siteName: acciSiteName } : null
    );
    const [companyModalOpen, setCompanyModalOpen] = useState(false);
    const [siteModalOpen, setSiteModalOpen] = useState(false);

    const realCompCode = isAdmin ? adminCompCode : auth?.compCode;
    const realSiteCode = isAdmin ? selectedSite?.siteCode : selectedUserSite;

    const {
        site, company, departmentList, centerEmployeeList, rightEmployeeList,
        selectedProcEqpt,
        setSelectedProcEqpt, setCenterEmployeeList, setRightEmployeeList,
        fetchInitialData, fetchCenterEmployeeList, fetchRightEmployeeList,
        handleSaveAll, handleCenterRowClick, handleRightRowClick,
        moveRightToCenter, moveCenterToRight,
    } = useSupvLogic(realCompCode,realSiteCode);

    useEffect(() => {
        if (!realSiteCode || !realCompCode) return;
        fetchInitialData();
    }, [realSiteCode, realCompCode, fetchInitialData]);

    useEffect(() => {
        if (!realSiteCode) {
            setSelectedProcEqpt(null);
            setCenterEmployeeList([]);
            setRightEmployeeList([]);
        }
    }, [realSiteCode, setSelectedProcEqpt, setCenterEmployeeList, setRightEmployeeList]);

    // 일반 사용자 사업장 목록 조회
    useEffect(() => {
        if (!isAdmin && auth?.compCode) {
            const fetchUserSites = async () => {
                try {
                    const data = await get("/user/procEqptInit", {
                        params: { compCode: auth.compCode },
                    });
                    if (data.siteList && data.siteList.length > 0) {
                        setUserSiteList(data.siteList);
                        const contextInList = acciSiteCode && data.siteList.some(s => s.siteCode === acciSiteCode);
                        if (!contextInList) {
                            const firstCode = data.siteList[0].siteCode;
                            const firstName = data.siteList[0].siteName;
                            setSelectedUserSite((prev) => prev || firstCode);
                            setAcciCompany(auth.compCode, auth.compName || "", firstCode, firstName);
                        }
                    }
                } catch (err) {
                    console.error("사업장 목록 조회 오류:", err);
                }
            };
            fetchUserSites();
        }
    }, [isAdmin, auth?.compCode]); // eslint-disable-line react-hooks/exhaustive-deps

    // 공정목록 클릭 시
    const handleClickProcessRow = async (item) => {
        setSelectedProcEqpt(item.mpreqCode);
        setRightEmployeeList([]);
        await fetchCenterEmployeeList(realSiteCode, item.mpreqCode);
    };

    // 오른쪽 끝 테이블 조회 
    const handleSearchEmployee= async () => {
        if (!selectedProcEqpt) {
            toast.warn("공정(설비)를 먼저 선택해주세요.");
            return;
        }
        await fetchRightEmployeeList({
            siteCodeParam: realSiteCode,
            mpreqCodeParam: selectedProcEqpt,
            deptCodeParam: deptCode,
            searchWrd: searchWrd,
        });
    };

    const onSaveAll = async () => {
        await handleSaveAll(realSiteCode, selectedProcEqpt, deptCode, searchWrd);
    };

    const showMainContent = isAdmin ? Boolean(realSiteCode) : true;

    // if (isLoading) return <div>로딩 중...</div>;
    // if (isError) return <div>오류가 발생했습니다: {error?.message}</div>;

    return (
        <FormProvider {...methods}>
            <form>
                <div className="contents-wrap">
                    <div className="page-title-wrap">
                        <h2>
                            <span className="icon-dot-circle"></span>관리감독자 - 공정·설비·직원
                        </h2>
                    </div>

                    <div className="search-wrap">
                        <div className="search-row">
                            <div className="search-item">
                                {isAdmin ? (
                                    <>
                                        <label>회사</label>
                                        <Input
                                            name="companyName"
                                            disabled
                                            value={adminCompName || ''}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setCompanyModalOpen(true)}
                                            className="search-icon"
                                        />
                                    </>
                                ) : (
                                    // 일반 사용자용: 사업장 선택만
                                    <>
                                        <label>사업장</label>
                                        <select
                                            value={selectedUserSite}
                                            onChange={(e) => {
                                                const siteCode = e.target.value;
                                                setSelectedUserSite(siteCode);
                                                const siteName = userSiteList.find(s => s.siteCode === siteCode)?.siteName || "";
                                                setAcciCompany(auth.compCode, auth.compName || "", siteCode, siteName);
                                            }}
                                            style={{
                                                padding: '8px 12px',
                                                width: '200px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <option value="">사업장을 선택하세요</option>
                                            {userSiteList.map(site => (
                                                <option key={site.siteCode} value={site.siteCode}>
                                                    {site.siteName}
                                                </option>
                                            ))}
                                        </select>
                                    </>
                                )}
                            </div>

                            {isAdmin && (
                                <div className="search-item">
                                    <label>사업장</label>
                                    <Input
                                        name="siteName"
                                        disabled
                                        value={selectedSite?.siteName || ''}
                                        placeholder={adminCompCode ? "사업장을 선택하세요" : "회사를 먼저 선택하세요"}
                                    />
                                    {adminCompCode && (
                                        <button
                                            type="button"
                                            onClick={() => setSiteModalOpen(true)}
                                            className="search-icon"
                                        />
                                    )}
                                </div>
                            )}
                            <div className="search-btn-container">
                                <div className="btn-right-box">
                                    <ul>
                                        {canWrite && <li><button type="button" title="저장" className="btn-save" onClick={onSaveAll}>저장</button></li>}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                {showMainContent ? (
                    /* 전체 레이아웃 (수평으로 N개 구역) */
                    <div className="total-layout">

                        {/* --- 1) 공정/설비 목록 테이블 --- */}
                        <ProcEqptList
                            siteCode={realSiteCode}
                            selectedProcEqpt={selectedProcEqpt}
                            onRowClick={handleClickProcessRow}
                        />

                        {/* --- 2) 설비 작업 관련 직원 --- */}
                        <section className="section-style">
                            <div className="section-tit-wrap" style={{height:"5%"}}>
                                <h3 className="border-left-primary"> &nbsp;설비 작업 관련 관리감독자</h3>
                            </div>

                            <div className="acc-table-wrap">
                                <table className="grid-list acc-list">
                                    <thead>
                                    <tr>
                                        <th style={{ width: "25px" }}></th>
                                        <th style={{ width: "100px"}}>이름</th>
                                        <th style={{ width: "50px" }}>관리감독자</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {centerEmployeeList.map((row) => (
                                        <tr
                                            key={row.empCode}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleCenterRowClick(row.empCode)}
                                        >
                                            <td>
                                                <Checkbox type="checkbox"
                                                          name="empCode"
                                                          readOnly
                                                          checked={row.isChecked ?? false}
                                                />
                                            </td>
                                            <td >{row.empName}</td>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={true}               // 항상 체크
                                                    onClick={(e) => {            // 클릭해도 토글 금지 + 부모 클릭 막기
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                    onChange={(e) => {           // 혹시나 change 발생해도 무시
                                                        e.preventDefault();
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        {/* --- 3) 화살표 버튼 (가운데 <-> 오른쪽) --- */}
                        <div className="arrow-btn-wrap">
                            {/* 오른쪽 → 가운데 */}
                            <button type="button" className="btn-add arrow-btn" onClick={moveRightToCenter}>◀</button>
                            {/* 가운데 → 오른쪽 */}
                            <button type="button" className="btn-add arrow-btn" onClick={moveCenterToRight}>▶</button>
                        </div>

                        {/* --- 4) 오른쪽 테이블(직원 목록) --- */}
                        <section className="section-style">
                            <div className="section-tit-wrap" style={{height:"5%"}}>
                                <h3 className="border-left-primary">&nbsp;직원 목록 </h3>
                            </div>
                            <div className="list-search-wrap" style={{height:"5%"}}>
                                <div className="list-search-item">
                                    <label>부서</label>
                                    <Select
                                        name="deptCode"
                                        options={departmentList}
                                        style={{ width:"100px"}}
                                        defaultOption="선택"
                                        className="input-select"
                                        valueKey="deptCode" // key 값
                                        labelKey="deptName" // 텍스트 보이는 이름
                                    />
                                    <label>이름</label>
                                    <Input
                                        name="searchWrd"
                                        placeholder="이름을 입력하세요"
                                        className="input-search"
                                        style={{ width: "150px" }}
                                    />
                                </div>
                                <div className="list-search-container">
                                    <button type="button" className="list-btn-search btn-add" onClick={handleSearchEmployee}>조회</button>
                                </div>
                            </div>

                            <div className="acc-table-wrap" style={{height: "90%"}} >
                                <table className="grid-list acc-list" >
                                    <thead>
                                    <tr>
                                        <th style={{ width: "40px" }}></th>
                                        <th style={{ width: "60px" }}>부서</th>
                                        <th style={{ width: "100px" }}>이름</th>
                                        <th style={{ width: "120px" }}>직위</th>
                                        <th style={{ width: "130px" }}>전화번호</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {rightEmployeeList.map((row) => (
                                        <tr
                                            key={row.empCode}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleRightRowClick(row.empCode)}
                                        >
                                            <td>
                                                <Checkbox type="checkbox"
                                                          name="empCode"
                                                          readOnly
                                                          checked={row.isChecked ?? false}
                                                />
                                            </td>
                                            <td>{row.deptName}</td>
                                            <td>{row.empName}</td>
                                            <td>{row.posName}</td>
                                            <td>{row.phone}</td>
                                        </tr>
                                    ))}

                                    {/* 데이터 없을 때 표시 */}
                                    {rightEmployeeList.length === 0 && (
                                        <tr>
                                            <td colSpan={5} style={{ textAlign: "center" }}>
                                                데이터가 없습니다.
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '50px', fontSize: '16px', color: '#666' }}>
                        위에서 회사와 사업장을 선택하세요.
                    </div>
                )}
                </div>
            </form>
            {/* 회사 선택 모달 */}
            {isAdmin && (
                <CompanySearchModal
                    isOpen={companyModalOpen}
                    onClose={() => setCompanyModalOpen(false)}
                    onConfirm={() => {
                        setSelectedSite(null);
                        clearAcciCompany();
                        setCompanyModalOpen(false);
                    }}
                />
            )}

            {/* 사업장 선택 모달 */}
            {isAdmin && (
                <SiteSearchModal
                    isOpen={siteModalOpen}
                    onClose={() => setSiteModalOpen(false)}
                    compCode={adminCompCode}
                    onConfirm={(site) => {
                        setSelectedSite(site);
                        setAcciCompany(adminCompCode, adminCompName, site.siteCode, site.siteName);
                        setSiteModalOpen(false);
                    }}
                />
            )}
        </FormProvider>
    );
};

export { Supv };
